import { booleanAttribute, ChangeDetectionStrategy, Component, forwardRef, input, model, output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ZagIcon } from '../icon/icon';
import { TooltipDirective, type TooltipPosition } from '../tooltip/tooltip.directive';

export interface ButtonGroupOption<T = string> {
  /** The value emitted when this option is selected */
  value: T;
  /** Primary display label (first line) */
  label: string;
  /** Optional secondary label shown on the second line */
  subLabel?: string;
  /** Optional zagIcon name rendered before the label */
  icon?: string;
  /** Optional tooltip text shown on hover/long-press — especially useful with `iconOnly` */
  tooltip?: string;
  /** Optional count rendered as a pill after the label and appended to the accessible name; 0 is shown */
  count?: number;
  /** Whether this individual option is disabled */
  disabled?: boolean;
}

export type ButtonGroupOrientation = 'vertical' | 'horizontal';

/**
 * A single-select toggle button group. Options are rendered as adjacent buttons
 * where only one can be active at a time.
 *
 * Supports both vertical (default, full-width stacked) and horizontal layouts.
 * Implements ControlValueAccessor for use with reactive forms.
 *
 * @example
 * ```html
 * <zag-button-group
 *   [options]="sortOptions"
 *   [(value)]="selectedSort"
 *   orientation="vertical"
 *   ariaLabel="Sort by"
 * />
 * ```
 */
@Component({
  selector: 'zag-button-group',
  imports: [ZagIcon, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="zag-button-group"
      [class.zag-button-group--vertical]="orientation() === 'vertical'"
      [class.zag-button-group--horizontal]="orientation() === 'horizontal'"
      [class.zag-button-group--stacked]="orientation() === 'horizontal' && stacked()"
      [class.zag-button-group--disabled]="disabled()"
      [class.zag-button-group--bare]="!bordered()"
      [class.zag-button-group--icon-only]="iconOnly()"
      role="group"
      [attr.aria-label]="ariaLabel() || null"
    >
      @for (option of options(); track option.value) {
        <button
          type="button"
          class="zag-button-group__btn"
          [class.zag-button-group__btn--active]="value() === option.value"
          [class.zag-button-group__btn--icon-only]="iconOnly()"
          [class.zag-button-group__btn--counted]="showCount(option)"
          [class.zag-button-group__btn--disabled]="disabled() || option.disabled"
          [disabled]="disabled() || option.disabled || null"
          [attr.aria-pressed]="value() === option.value"
          [attr.aria-label]="iconOnly() ? accessibleName(option) : null"
          [zagTooltip]="option.tooltip ?? (iconOnly() ? option.label : null)"
          [tooltipPosition]="tooltipPosition()"
          (click)="onSelect(option.value)"
        >
          @if (option.icon) {
            <span [zagIcon]="option.icon" class="zag-button-group__icon" aria-hidden="true"></span>
          }
          @if (option.label) {
            <span class="zag-button-group__label" [class.zag-sr-only]="iconOnly()">
              <span class="zag-button-group__label-text">{{ option.label }}</span>
              @if (option.subLabel) {
                <span class="zag-button-group__sub-label">{{ option.subLabel }}</span>
              }
            </span>
          }
          @if (showCount(option)) {
            <span class="zag-button-group__count">{{ option.count }}</span>
          }
          @if (orientation() === 'vertical' && value() === option.value) {
            <span class="zag-button-group__check" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
              </svg>
            </span>
          }
        </button>
      }
    </div>
  `,
  styleUrl: './button-group.component.scss',
  host: {
    class: 'zag-button-group-host',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonGroupComponent),
      multi: true,
    },
  ],
})
export class ButtonGroupComponent<T = string> implements ControlValueAccessor {
  /** Options to render as toggle buttons */
  readonly options = input.required<ButtonGroupOption<T>[]>();

  /** Currently selected value (two-way binding via model) */
  readonly value = model<T | undefined>(undefined);

  /** Layout direction: vertical (default) or horizontal */
  readonly orientation = input<ButtonGroupOrientation>('vertical');

  readonly stacked = input(false, { transform: booleanAttribute });

  /** Disables all buttons */
  readonly disabled = input(false, { transform: booleanAttribute });

  /** Without the outer border and radius, for embedding in a parent-owned bordered container. */
  readonly bordered = input(true, { transform: booleanAttribute });

  /** When true, only icons are shown and labels are visually hidden (sr-only) */
  readonly iconOnly = input(false, { transform: booleanAttribute });

  /** Position of per-option tooltips relative to the button */
  readonly tooltipPosition = input<TooltipPosition>('above');

  /** Accessible label for the group element */
  readonly ariaLabel = input<string>('');

  /** Emitted when the selected value changes */
  readonly selectionChange = output<T | undefined>();

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: T | undefined) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  protected showCount(option: ButtonGroupOption<T>): boolean {
    return option.count !== undefined && !this.iconOnly();
  }

  protected accessibleName(option: ButtonGroupOption<T>): string {
    return option.count === undefined ? option.label : `${option.label} ${option.count}`;
  }

  protected onSelect(optionValue: T): void {
    this.value.set(optionValue);
    this.onChange(optionValue);
    this.onTouched();
    this.selectionChange.emit(optionValue);
  }

  // ─── ControlValueAccessor ────────────────────────────────────────────────────

  writeValue(val: T | undefined): void {
    // model() signal update triggers OnPush re-render automatically
    this.value.set(val);
  }

  registerOnChange(fn: (value: T | undefined) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    // disabled input() signal triggers OnPush re-render automatically; no manual markForCheck needed
  }
}
